import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AxiosInstance from "../Home/AxiosInstance";
import { storeTokens, buildTraineeSignInPayload } from "../Home/auth";
import TraineeHeader from "../Trainee/TraineeHeader";
import TraineeCatalogHero from "../Trainee/TraineeCatalogHero";
import Header from "../Home/Header";
import Footers from "../Home/Footers";
import "./Course.css";
import "../Home/publicPages.css";
import "../Home/catalogHero.css";
import Course from "./Course.jpg";
import { FiArrowRight, FiSearch, FiClock, FiMapPin } from "react-icons/fi";
import { FaTimes, FaStar, FaStarHalfAlt, FaRegStar } from "react-icons/fa";
import { BsLaptop, BsBuilding } from "react-icons/bs";
import {
  CatalogFilters,
  FilterGroup,
  FilterCheckbox,
  FilterPriceRange,
  toggleFilterValue,
  buildCourseFilterOptions,
  matchesExactValues,
  matchesCoursePriceMax,
} from "../Home/CatalogFilters";

export default function Courses() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyMap, setCompanyMap] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const isFromTraineeProfile =
    location.state?.from === "trainee-profile" ||
    Boolean(localStorage.getItem("traineeId"));

  const [searchCourseName, setSearchCourseName] = useState("");
  const [searchCompanyName, setSearchCompanyName] = useState("");
  const [searchTrainerName, setSearchTrainerName] = useState("");
  const [searchLocation, setSearchLocation] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isSignupMode, setIsSignupMode] = useState(false);
  const [enrolledBatchIds, setEnrolledBatchIds] = useState(() => new Set());
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [modalBatchId, setModalBatchId] = useState(null);

  const [filters, setFilters] = useState({
    courseName: "",
    companyName: "",
    trainerName: "",
    location: "",
  });

  const [sideFilters, setSideFilters] = useState({
    courseNames: [],
    modes: [],
    batchTypes: [],
    timezones: [],
    durations: [],
    priceMax: null,
  });

  const [courseNameSuggestions, setCourseNameSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const coursesPerPage = 9;

  useEffect(() => {
    AxiosInstance.get("companies/batches/")
      .then(async (response) => {
        const publishedCourses = response.data.filter(
          (course) => course.is_published === "Yes"
        );
        setCourses(publishedCourses);

        const courseNames = [
          ...new Set(
            publishedCourses
              .map((c) => c.course_material?.course?.course_name)
              .filter(Boolean)
          ),
        ];
        setCourseNameSuggestions(courseNames.sort());

        const uniqueUserIds = [
          ...new Set(
            publishedCourses
              .map(
                (course) =>
                  course.course_material?.user?.user_id || course.user?.user_id
              )
              .filter(Boolean)
          ),
        ];

        const tempCompanyMap = {};
        await Promise.all(
          uniqueUserIds.map(async (uid) => {
            try {
              const res = await AxiosInstance.get(
                `companies/company/user/${uid}/`
              );
              const companyName =
                res.data?.company_users?.[0]?.company?.company_name;
              if (companyName) tempCompanyMap[uid] = companyName;
            } catch (err) {
              console.error(`Error fetching company for user ${uid}:`, err);
            }
          })
        );

        setCompanyMap(tempCompanyMap);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching course data:", error);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const traineeId = localStorage.getItem("traineeId");
    if (!traineeId) {
      setEnrolledBatchIds(new Set());
      return;
    }
    AxiosInstance.get(`companies/trainee/enrollments/${traineeId}/`)
      .then((res) => {
        const ids = new Set(
          (Array.isArray(res.data) ? res.data : [])
            .filter((e) => e.is_enrolled === "Enrolled")
            .map((e) => e.batch?.batch_id ?? e.batch)
            .filter(Boolean)
            .map((id) => Number(id))
        );
        setEnrolledBatchIds(ids);
      })
      .catch(() => setEnrolledBatchIds(new Set()));
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, sideFilters]);

  const handleSearch = () => {
    setFilters({
      courseName: searchCourseName,
      companyName: searchCompanyName,
      trainerName: searchTrainerName,
      location: searchLocation,
    });
  };

  const clearCourseSearch = () => {
    setSearchCourseName("");
    setShowSuggestions(false);
    setFilters((prev) => ({
      ...prev,
      courseName: "",
      trainerName: "",
    }));
  };

  const clearLocationSearch = () => {
    setSearchLocation("");
    setFilters((prev) => ({
      ...prev,
      location: "",
    }));
  };

  const handleClear = () => {
    setSearchCourseName("");
    setSearchCompanyName("");
    setSearchTrainerName("");
    setSearchLocation("");
    setFilters({
      courseName: "",
      companyName: "",
      trainerName: "",
      location: "",
    });
    setSideFilters({
      courseNames: [],
      modes: [],
      batchTypes: [],
      timezones: [],
      durations: [],
      priceMax: null,
    });
  };

  const toggleSideFilter = (key, value) => {
    setSideFilters((prev) => ({
      ...prev,
      [key]: toggleFilterValue(prev[key], value),
    }));
  };

  const handleViewContent = (batchId, courseMaterialId) => {
    localStorage.setItem("course_material_id", courseMaterialId);
    navigate(`/courses/${batchId}`, {
      state: { from: location.state?.from },
    });
  };

  const handleViewProfile = (id) =>
    navigate(`/profiles/${id}`, { state: { from: "/courses" } });

  const availableFilters = useMemo(
    () => buildCourseFilterOptions(courses),
    [courses]
  );

  const filteredCourses = courses.filter((course) => {
    const material = course.course_material;
    const courseData = material?.course;
    const trainer = material?.trainer;
    const user = material?.user || course.user;

    const courseName = courseData?.course_name?.toLowerCase() || "";
    const company = user?.user_id
      ? companyMap[user.user_id]?.toLowerCase() || ""
      : "";
    const trainerName = (() => {
      if (trainer) {
        return (
          trainer.alias_name?.toLowerCase() ||
          `${trainer.first_name || ""} ${trainer.last_name || ""}`.toLowerCase()
        );
      } else if (user) {
        return (
          user.alias_name?.toLowerCase() ||
          `${user.first_name || ""} ${user.last_name || ""}`.toLowerCase()
        );
      }
      return "";
    })();

    const venueAddress = course.venue_address?.toLowerCase() || "";
    const searchCourseOrTrainer = filters.courseName.toLowerCase();
    const courseNameOrTrainerMatch =
      courseName.includes(searchCourseOrTrainer) ||
      trainerName.includes(searchCourseOrTrainer);

    const courseNameMatch = matchesExactValues(
      courseData?.course_name,
      sideFilters.courseNames
    );
    const modeMatch = matchesExactValues(course.mode, sideFilters.modes);
    const batchTypeMatch = matchesExactValues(
      course.batch_name,
      sideFilters.batchTypes
    );
    const timezoneMatch = matchesExactValues(
      course.timezone,
      sideFilters.timezones
    );
    const durationMatch = matchesExactValues(
      course.duration,
      sideFilters.durations
    );
    const priceMatch = matchesCoursePriceMax(
      course.fee,
      sideFilters.priceMax,
      availableFilters.priceCeiling
    );

    return (
      courseNameOrTrainerMatch &&
      company.includes(filters.companyName.toLowerCase()) &&
      venueAddress.includes(filters.location.toLowerCase()) &&
      courseNameMatch &&
      modeMatch &&
      batchTypeMatch &&
      timezoneMatch &&
      durationMatch &&
      priceMatch
    );
  });

  const upcomingCourses = filteredCourses
    .filter((course) => new Date(course.batch_starts_at) > new Date())
    .sort((a, b) => new Date(a.batch_starts_at) - new Date(b.batch_starts_at));

  const totalPages = Math.ceil(upcomingCourses.length / coursesPerPage);
  const indexOfLastCourse = currentPage * coursesPerPage;
  const indexOfFirstCourse = indexOfLastCourse - coursesPerPage;
  const currentCourses = upcomingCourses.slice(
    indexOfFirstCourse,
    indexOfLastCourse
  );

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    mobile_no: "",
  });

  const handleChange = (e) => {
    const key = e.target.name || e.target.id;
    setFormData({ ...formData, [key]: e.target.value });
  };

  const handleShowModal = (batchId) => {
    setModalBatchId(batchId);
  };

  const resetForm = () => {
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      mobile_no: "",
    });
  };

  const handleCloseModal = () => {
    setModalBatchId(null);
    setShowModal(false);
    resetForm();
  };

  // Signup & Enroll
  const handleSignup = async (e) => {
    e.preventDefault();
    const { first_name, last_name, email, password, mobile_no } = formData;
    if (!first_name || !last_name || !email || !password || !mobile_no) {
      alert("Please fill all fields");
      return;
    }
    try {
      const signupRes = await AxiosInstance.post("companies/signup", formData);
      const traineeId = signupRes.data.trainee_id;
      storeTokens(signupRes.data);
      localStorage.setItem("traineeId", traineeId);
      await enrollUser(traineeId, selectedBatchId);
    } catch (err) {
      console.error(err);

      if (err.response) {
        const data = err.response.data;

        let errorMessage = "Something went wrong";

        if (data.error) {
          const errorObj = data.error;
          const firstKey = Object.keys(errorObj)[0];
          errorMessage = Array.isArray(errorObj[firstKey])
            ? errorObj[firstKey][0]
            : errorObj[firstKey];
        }

        alert(errorMessage);
      } else {
        alert("Something went wrong during signup.");
      }
    }
  };

  // Sign In & Enroll
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      alert("Please enter email or mobile number and password");
      return;
    }
    try {
      const signinRes = await AxiosInstance.post(
        "companies/signin",
        buildTraineeSignInPayload(formData.email, formData.password)
      );
      const { trainee } = signinRes.data;
      const traineeId = trainee?.trainee_id;
      if (traineeId) {
        storeTokens(signinRes.data);
        localStorage.setItem("traineeId", traineeId);
        await enrollUser(traineeId, selectedBatchId);
      } else {
        alert("Invalid login response. Trainee ID missing.");
      }
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Sign in failed");
    }
  };

  // ENROLL FUNCTION
  const enrollUser = async (traineeId, batchId) => {
    const enrollmentPayload = {
      is_enrolled: "Enrolled",
      enrolled_at: new Date().toISOString(),
      batch: parseInt(batchId),
      trainee: parseInt(traineeId),
    };

    try {
      const enrollRes = await AxiosInstance.post(
        "companies/enrollment/",
        enrollmentPayload
      );
      setEnrolledBatchIds((prev) => {
        const next = new Set(prev);
        next.add(Number(batchId));
        return next;
      });
      setShowModal(false);
      alert("Enrollment successful!");
      resetForm();
      navigate("/trainee-profile");
    } catch (err) {
      console.error(err);

      if (err.response?.status === 409) {
        const backendError = err.response?.data?.error;

        setModalBatchId(null);
        setShowModal(false);
        resetForm();

        // Batch full
        if (backendError === "Batch is already full") {
          alert(`Batch ${batchId} is already full.`);
          return;
        }

        // Already enrolled
        if (backendError === "Trainee is already enrolled in this batch") {
          setEnrolledBatchIds((prev) => {
            const next = new Set(prev);
            next.add(Number(batchId));
            return next;
          });
          alert("You are already enrolled in this batch.");
          return;
        }

        alert(backendError || "Conflict occurred");
        return;
      }
      // default failure
      alert(err.response?.data?.error || "Enrollment failed");
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      if (rating >= i) stars.push(<FaStar key={i} />);
      else if (rating >= i - 0.5) stars.push(<FaStarHalfAlt key={i} />);
      else stars.push(<FaRegStar key={i} />);
    }
    return stars;
  };

  const handleEnrollClick = async (batchId) => {
    if (enrolledBatchIds.has(Number(batchId))) return;
    if (isFromTraineeProfile) {
      const confirmed = window.confirm(
        "Do you want to enroll into the course?"
      );
      if (confirmed) {
        const traineeId = localStorage.getItem("traineeId");
        if (traineeId) {
          await enrollUser(traineeId, batchId);
        } else {
          alert("Trainee ID not found. Please sign in again.");
        }
      }
    } else {
      setSelectedBatchId(batchId);
      setModalBatchId(batchId);
    }
  };

  return (
    <div className="CoursePage d-flex flex-column min-vh-100">
      {isFromTraineeProfile ? <TraineeHeader /> : <Header />}
      {isFromTraineeProfile && (
        <TraineeCatalogHero
          eyebrow="Courses"
          title="Explore. Learn. Grow your skills."
          subtitle="Learn from expert trainers and institutes — find the right program to grow your career."
        />
      )}
      {!isFromTraineeProfile && (
        <div className="als-page-hero als-hero-split">
          <div className="als-page-hero-inner">
            <div className="als-hero-copy">
              <h1>Explore. Learn. Grow your skills.</h1>
              <p>
                Learn from expert trainers and institutes — find the right
                program to grow your career.
              </p>
            </div>
            <div className="als-hero-media">
              <img
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&h=520&fit=crop"
                alt="Students learning together"
              />
            </div>
          </div>
        </div>
      )}

      <div
        className={`course-catalog flex-grow-1${
          isFromTraineeProfile ? " course-catalog--trainee" : ""
        }`}
      >
        <div className="course-catalog-inner">
          {/* Marketplace search */}
          <form
            className="ud-search-bar"
            onSubmit={(e) => {
              e.preventDefault();
              handleSearch();
            }}
          >
            <div className="ud-search-field">
              <FiSearch className="ud-search-icon" />
              <input
                type="text"
                placeholder="Search for anything — course or trainer"
                value={searchCourseName}
                onChange={(e) => {
                  setSearchCourseName(e.target.value);
                  setShowSuggestions(true);
                }}
                autoComplete="off"
              />
              {searchCourseName && (
                <button
                  type="button"
                  className="filter-clear-icon"
                  aria-label="Clear course search"
                  onClick={clearCourseSearch}
                >
                  <FaTimes size={12} />
                </button>
              )}
              {searchCourseName && showSuggestions && (
                <ul className="list-group course-suggestions">
                  {courseNameSuggestions
                    .filter((name) =>
                      name
                        .toLowerCase()
                        .includes(searchCourseName.toLowerCase())
                    )
                    .slice(0, 8)
                    .map((name, index) => (
                      <li
                        key={index}
                        className="list-group-item list-group-item-action"
                        onClick={() => {
                          setSearchCourseName(name);
                          setShowSuggestions(false);
                          setFilters((prev) => ({
                            ...prev,
                            courseName: name,
                          }));
                        }}
                      >
                        {name}
                      </li>
                    ))}
                </ul>
              )}
            </div>
            <div className="ud-search-field ud-search-location">
              <FiMapPin className="ud-search-icon" />
              <input
                type="text"
                placeholder="Location"
                value={searchLocation}
                onChange={(e) => setSearchLocation(e.target.value)}
              />
              {searchLocation && (
                <button
                  type="button"
                  className="filter-clear-icon"
                  aria-label="Clear location"
                  onClick={clearLocationSearch}
                >
                  <FaTimes size={12} />
                </button>
              )}
            </div>
            <button type="submit" className="ud-search-btn">
              Search
            </button>
          </form>

          <div className="ud-catalog-layout">
            {/* Filters — Coursera / Udemy style */}
            <CatalogFilters onClear={handleClear}>
              {availableFilters.courseNames.length > 0 && (
                <FilterGroup title="Course">
                  {availableFilters.courseNames.map((name) => (
                    <FilterCheckbox
                      key={name}
                      label={name}
                      checked={sideFilters.courseNames.includes(name)}
                      onChange={() => toggleSideFilter("courseNames", name)}
                    />
                  ))}
                </FilterGroup>
              )}
              {availableFilters.modes.length > 0 && (
                <FilterGroup title="Mode">
                  {availableFilters.modes.map((mode) => (
                    <FilterCheckbox
                      key={mode}
                      label={mode}
                      checked={sideFilters.modes.includes(mode)}
                      onChange={() => toggleSideFilter("modes", mode)}
                    />
                  ))}
                </FilterGroup>
              )}
              {availableFilters.batchTypes.length > 0 && (
                <FilterGroup title="Batch type">
                  {availableFilters.batchTypes.map((batch) => (
                    <FilterCheckbox
                      key={batch}
                      label={batch}
                      checked={sideFilters.batchTypes.includes(batch)}
                      onChange={() => toggleSideFilter("batchTypes", batch)}
                    />
                  ))}
                </FilterGroup>
              )}
              {availableFilters.timezones.length > 0 && (
                <FilterGroup title="Timezone">
                  {availableFilters.timezones.map((tz) => (
                    <FilterCheckbox
                      key={tz}
                      label={tz}
                      checked={sideFilters.timezones.includes(tz)}
                      onChange={() => toggleSideFilter("timezones", tz)}
                    />
                  ))}
                </FilterGroup>
              )}
              {availableFilters.durations.length > 0 && (
                <FilterGroup title="Duration">
                  {availableFilters.durations.map((duration) => (
                    <FilterCheckbox
                      key={duration}
                      label={duration}
                      checked={sideFilters.durations.includes(duration)}
                      onChange={() => toggleSideFilter("durations", duration)}
                    />
                  ))}
                </FilterGroup>
              )}
              {availableFilters.priceCeiling > 0 && (
                <FilterGroup title="Price">
                  <FilterPriceRange
                    min={0}
                    max={availableFilters.priceCeiling}
                    value={
                      sideFilters.priceMax ?? availableFilters.priceCeiling
                    }
                    onChange={(priceMax) =>
                      setSideFilters((prev) => ({ ...prev, priceMax }))
                    }
                  />
                </FilterGroup>
              )}
            </CatalogFilters>

            {/* Results */}
            <div className="ud-results">
              <div className="ud-results-bar">
                <h2>
                  {loading
                    ? "Loading courses…"
                    : `${upcomingCourses.length} result${
                        upcomingCourses.length === 1 ? "" : "s"
                      } for “${filters.courseName || "all courses"}”`}
                </h2>
              </div>

              {loading ? (
                <div className="ud-empty">Loading courses…</div>
              ) : upcomingCourses.length === 0 ? (
                <div className="ud-empty">
                  <h3>No courses found</h3>
                  <p>Try adjusting your search or filters.</p>
                  <button type="button" className="ud-search-btn" onClick={handleClear}>
                    Clear filters
                  </button>
                </div>
              ) : (
                <>
                  <div className="ud-course-grid">
                    {currentCourses.map((courseGroup, index) => {
                      const material = courseGroup.course_material;
                      const course = material?.course;
                      const trainer = material?.trainer;
                      const user = material?.user || courseGroup.user;
                      const profile = material?.profile;
                      const companyName = user?.user_id
                        ? companyMap[user.user_id]
                        : "";

                      const trainerName = (() => {
                        const getValidName = (obj) => {
                          if (!obj) return null;
                          if (
                            obj.alias_name &&
                            obj.alias_name.trim().toLowerCase() !== "null"
                          ) {
                            return obj.alias_name.trim();
                          }
                          const fullName = `${obj.first_name || ""} ${
                            obj.last_name || ""
                          }`.trim();
                          return fullName || null;
                        };
                        return (
                          getValidName(trainer) || getValidName(user) || "Instructor"
                        );
                      })();

                      const rating = 4.5 + ((index % 5) * 0.1);
                      const reviews = 120 + index * 37;
                      const logoSrc = course?.course_logo
                        ? course.course_logo.startsWith("http")
                          ? course.course_logo
                          : `${AxiosInstance.defaults.baseURL}media/${course.course_logo}`
                        : Course;

                      return (
                        <article
                          key={courseGroup.batch_id || index}
                          className="ud-course-card"
                        >
                          <button
                            type="button"
                            className="ud-course-media"
                            onClick={() =>
                              handleViewContent(
                                courseGroup.batch_id,
                                material?.course_material_id
                              )
                            }
                          >
                            <img
                              src={logoSrc}
                              alt={course?.course_name || "Course"}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = Course;
                              }}
                            />
                            {index === 0 && (
                              <span className="ud-badge">Bestseller</span>
                            )}
                            {courseGroup.mode && (
                              <span className="ud-mode-chip">
                                {courseGroup.mode === "Online" ? (
                                  <BsLaptop />
                                ) : (
                                  <BsBuilding />
                                )}
                                {courseGroup.mode}
                              </span>
                            )}
                          </button>

                          <div className="ud-course-body">
                            <h3
                              className="ud-course-title"
                              onClick={() =>
                                handleViewContent(
                                  courseGroup.batch_id,
                                  material?.course_material_id
                                )
                              }
                            >
                              {course?.course_name || "Untitled course"}
                            </h3>

                            <p className="ud-course-meta-line">
                              {companyName ? (
                                <>
                                  {companyName}
                                  {" · "}
                                </>
                              ) : null}
                              {profile ? (
                                <button
                                  type="button"
                                  className="ud-inline-link"
                                  onClick={() =>
                                    handleViewProfile(profile.profile_id)
                                  }
                                >
                                  {trainerName}
                                </button>
                              ) : (
                                trainerName
                              )}
                            </p>

                            <div className="ud-rating">
                              <strong>{rating.toFixed(1)}</strong>
                              <span className="ud-stars">{renderStars(rating)}</span>
                              <span className="ud-reviews">({reviews})</span>
                            </div>

                            <div className="ud-chips">
                              {courseGroup.duration && (
                                <span>
                                  <FiClock /> {courseGroup.duration}
                                </span>
                              )}
                              {courseGroup.batch_name && (
                                <span>{courseGroup.batch_name}</span>
                              )}
                              {courseGroup.batch_starts_at && (
                                <span>Starts {courseGroup.batch_starts_at}</span>
                              )}
                            </div>

                            {courseGroup.mode === "Offline" &&
                              courseGroup.venue_address && (
                                <p className="ud-venue">
                                  <FiMapPin /> {courseGroup.venue_address}
                                </p>
                              )}

                            <div className="ud-card-actions">
                              <button
                                type="button"
                                className="ud-link-btn"
                                onClick={() =>
                                  handleViewContent(
                                    courseGroup.batch_id,
                                    material?.course_material_id
                                  )
                                }
                              >
                                View content <FiArrowRight />
                              </button>
                              {!enrolledBatchIds.has(Number(courseGroup.batch_id)) ? (
                                <button
                                  type="button"
                                  className="ud-enroll-btn"
                                  onClick={() =>
                                    handleEnrollClick(courseGroup.batch_id)
                                  }
                                >
                                  Enroll
                                </button>
                              ) : (
                                <button type="button" className="ud-enroll-btn enrolled" disabled>
                                  Enrolled
                                </button>
                              )}
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>

                  {modalBatchId && (
                    <div className="Overlay" onClick={handleCloseModal}>
                      <div
                        className="enroll-modal"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="enroll-modal-title"
                      >
                        <button
                          type="button"
                          className="enroll-modal-close"
                          onClick={handleCloseModal}
                          aria-label="Close"
                        >
                          <FaTimes />
                        </button>
                        <h2 id="enroll-modal-title">
                          {isSignupMode
                            ? "Sign up to enroll"
                            : "Log in to enroll"}
                        </h2>
                        <p className="enroll-modal-sub">
                          {isSignupMode
                            ? "Create an account to continue your enrollment."
                            : "Welcome back. Sign in to continue enrollment."}
                        </p>
                        <form
                          onSubmit={isSignupMode ? handleSignup : handleSubmit}
                        >
                          {isSignupMode && (
                            <>
                              <div className="enroll-field-row">
                                <div className="enroll-field">
                                  <label htmlFor="first_name">First name</label>
                                  <input
                                    type="text"
                                    id="first_name"
                                    value={formData.first_name || ""}
                                    onChange={handleChange}
                                    placeholder="First name"
                                    required
                                  />
                                </div>
                                <div className="enroll-field">
                                  <label htmlFor="last_name">Last name</label>
                                  <input
                                    type="text"
                                    id="last_name"
                                    value={formData.last_name || ""}
                                    onChange={handleChange}
                                    placeholder="Last name"
                                    required
                                  />
                                </div>
                              </div>
                              <div className="enroll-field">
                                <label htmlFor="mobile_no">Mobile number</label>
                                <input
                                  type="tel"
                                  id="mobile_no"
                                  value={formData.mobile_no || ""}
                                  onChange={handleChange}
                                  placeholder="Mobile number"
                                  required
                                />
                              </div>
                            </>
                          )}
                          <div className="enroll-field">
                            <label htmlFor={`email-${modalBatchId}`}>
                              {isSignupMode
                                ? "Email"
                                : "Email or mobile number"}
                            </label>
                            <input
                              id={`email-${modalBatchId}`}
                              name="email"
                              type={isSignupMode ? "email" : "text"}
                              value={formData.email}
                              onChange={handleChange}
                              placeholder={
                                isSignupMode
                                  ? "Email"
                                  : "Email or mobile number"
                              }
                              autoComplete="username"
                              required
                            />
                          </div>
                          <div className="enroll-field">
                            <label htmlFor="password">Password</label>
                            <input
                              type="password"
                              id="password"
                              name="password"
                              value={formData.password}
                              onChange={handleChange}
                              placeholder="Password"
                              required
                            />
                          </div>
                          <button type="submit" className="enroll-modal-submit">
                            {isSignupMode
                              ? "Sign up & enroll"
                              : "Log in & enroll"}
                          </button>
                        </form>
                        <p className="enroll-modal-switch">
                          {isSignupMode ? (
                            <>
                              Already have an account?{" "}
                              <button
                                type="button"
                                onClick={() => setIsSignupMode(false)}
                              >
                                Log in
                              </button>
                            </>
                          ) : (
                            <>
                              Don&apos;t have an account?{" "}
                              <button
                                type="button"
                                onClick={() => setIsSignupMode(true)}
                              >
                                Sign up
                              </button>
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                  )}

                  {totalPages > 1 && (
                    <div className="ud-pagination">
                      {[...Array(totalPages)].map((_, idx) => {
                        const pageNum = idx + 1;
                        return (
                          <button
                            key={pageNum}
                            type="button"
                            className={`ud-page-btn ${
                              pageNum === currentPage ? "active" : ""
                            }`}
                            onClick={() => handlePageChange(pageNum)}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footers />
    </div>
  );
}
